/// <reference types="vite/client" />

// ═══════════════════════════════════════════════════════
// CORE — Supabase client + utility helpers
//
// IMPORTANT: All exports use `export function` (not `export const`)
// because function declarations are HOISTED by the JS engine.
// This makes them immune to Temporal Dead Zone (TDZ) errors
// when Rollup reorders declarations in flat production bundles.
//
// The supabase client uses lazy initialization to avoid calling
// createClient() at module init time, which is TDZ-susceptible
// when Rollup reorders the createClient import binding.
// ═══════════════════════════════════════════════════════
import { createClient, SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    "[LPA] Variáveis de ambiente ausentes: VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY " +
      "devem estar definidas em .env.local (dev) ou nas Environment Variables do Vercel (prod)."
  );
}

// ── Lazy-initialized Supabase client ─────────────────
let _supabase: SupabaseClient | null = null;

function _getSupabase(): SupabaseClient {
  if (!_supabase) {
    _supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
      global: {
        headers: { "x-application-name": "lpa-portal" },
      },
    });
  }
  return _supabase;
}

// For backward compatibility: `supabase` is a getter-backed const
// so existing `import { supabase } from "./core.js"` still works.
// The getter ensures createClient() is called at first USE, not at IMPORT.
// @ts-expect-error Proxy intercepts all access; empty target is intentional
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target: object, prop: string | symbol) {
    return (_getSupabase() as unknown as Record<string | symbol, unknown>)[prop];
  },
  set(_target: object, prop: string | symbol, value: unknown) {
    (_getSupabase() as unknown as Record<string | symbol, unknown>)[prop] = value;
    return true;
  },
});

/**
 * Retry helper: tenta uma operação async até `attempts` vezes com backoff exponencial.
 * Ignora erros de "not found" (PGRST116) sem retry.
 */
export function withRetry<T>(
  fn: () => Promise<T>,
  attempts: number = 3,
  baseDelay: number = 400
): Promise<T> {
  let lastError: unknown;
  const run = async (): Promise<T> => {
    for (let i = 0; i < attempts; i++) {
      try {
        return await fn();
      } catch (e: unknown) {
        lastError = e;
        const err = e as { code?: string; status?: number };
        if (err.code === "PGRST116" || err.code === "PGRST301" || err.status === 401) throw e;
        if (i < attempts - 1) {
          await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, i)));
        }
      }
    }
    throw lastError;
  };
  return run();
}

// Merge helpers for snapshot upsert
export function m<T>(nv: T | null | undefined, ev: T | null | undefined): T | null {
  return nv != null && nv !== (0 as unknown as T) && nv !== ("" as unknown as T)
    ? nv
    : (ev ?? null);
}

export function mStr(nv: string | null | undefined, ev: string | null | undefined): string | null {
  return nv != null && nv !== "" ? nv : (ev ?? null);
}

export function mArr<T>(nv: T[] | null | undefined, ev: T[] | null | undefined): T[] | null {
  return Array.isArray(nv) && nv.length > 0
    ? nv
    : Array.isArray(ev) && ev.length > 0
      ? ev
      : (nv ?? null);
}

export function mObj<T extends Record<string, unknown>>(
  nv: T | null | undefined,
  ev: T | null | undefined
): T | null {
  return nv && typeof nv === "object" && Object.keys(nv).length > 0
    ? nv
    : ev && typeof ev === "object"
      ? ev
      : (nv ?? null);
}
